import { CAMERAS_API_URL } from 'constants/ApiConstants';
import type { ApiError, MetaAction } from 'modules/global/generic.types';

import * as types from './actionTypes';
import type * as CameraTypes from './types';

export const fetchCameras = (
  params: CameraTypes.CamerasGetRequest,
  meta: MetaAction = {}
): CameraTypes.FetchCamerasAction => ({
  type: types.FETCH_CAMERAS,
  payload: {
    request: {
      method: 'GET',
      url: CAMERAS_API_URL,
      params,
    },
  },
  meta,
});

export const fetchCamerasSuccess = (
  data: CameraTypes.CamerasPage,
  meta: MetaAction = {}
): CameraTypes.FetchCamerasSuccessAction => ({
  type: types.FETCH_CAMERAS_SUCCESS,
  payload: data,
  meta,
});

export const fetchCamera = (cameraId: string, meta: MetaAction = {}): CameraTypes.FetchCameraAction => ({
  type: types.FETCH_CAMERA,
  payload: {
    cameraId,
    request: {
      method: 'GET',
      url: `${CAMERAS_API_URL}/${cameraId}`,
    },
  },
  meta,
});

export const fetchCameraSuccess = (
  camera: CameraTypes.DetailedCamera,
  meta: MetaAction = {}
): CameraTypes.FetchCameraSuccessAction => ({
  type: types.FETCH_CAMERA_SUCCESS,
  payload: camera,
  meta,
});

export const fetchCameraFailure = (error: ApiError, meta: MetaAction = {}): CameraTypes.FetchCameraFailureAction => ({
  type: types.FETCH_CAMERA_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const createCamera = (
  camera: CameraTypes.CameraCreateData,
  meta: MetaAction = {}
): CameraTypes.CreateCameraAction => ({
  type: types.POST_CAMERA,
  payload: {
    request: {
      method: 'POST',
      url: CAMERAS_API_URL,
      data: camera,
    },
  },
  meta,
});

export const pairTv = (data: CameraTypes.PairTvData, meta: MetaAction = {}): CameraTypes.PairTvAction => ({
  type: types.POST_CAMERA_TV_PAIRING,
  payload: {
    request: {
      method: 'POST',
      url: `${CAMERAS_API_URL}/${data.cameraId}/pair`,
      data,
    },
  },
  meta,
});

export const unpairTv = (data: CameraTypes.PairTvData, meta: MetaAction = {}): CameraTypes.UnpairTvAction => ({
  type: types.POST_CAMERA_TV_UNPAIRING,
  payload: {
    request: {
      method: 'POST',
      url: `${CAMERAS_API_URL}/${data.cameraId}/unpair`,
      data,
    },
  },
  meta,
});

export const createCameraSuccess = (
  camera: CameraTypes.DetailedCamera,
  meta: MetaAction = {}
): CameraTypes.CreateCameraSuccessAction => ({
  type: types.POST_CAMERA_SUCCESS,
  payload: camera,
  meta,
});

export const updateCamera = (
  camera: CameraTypes.DetailedCamera,
  meta: MetaAction = {}
): CameraTypes.PutCameraAction => ({
  type: types.PUT_CAMERA,
  payload: {
    request: {
      method: 'PUT',
      url: `${CAMERAS_API_URL}/${camera.id}`,
      data: camera,
    },
  },
  meta,
});

export const updateCameraSuccess = (
  camera: CameraTypes.DetailedCamera,
  meta: MetaAction = {}
): CameraTypes.PutCameraSuccessAction => ({
  type: types.PUT_CAMERA_SUCCESS,
  payload: camera,
  meta,
});

export const patchCameraStatus = (
  { cameraId, status }: CameraTypes.PatchCameraStatusData,
  meta: MetaAction = {}
): CameraTypes.PatchCameraStatusAction => ({
  type: types.PATCH_STATUS_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${CAMERAS_API_URL}/${cameraId}/status`,
      data: { status },
    },
    cameraId,
  },
  meta,
});

export const patchCameraStatusFailure = (
  error: ApiError,
  meta: MetaAction = {}
): CameraTypes.PatchCameraStatusFailureAction => ({
  type: types.PATCH_STATUS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const deleteCamera = (cameraId: string, meta: MetaAction = {}): CameraTypes.DeleteCameraAction => ({
  type: types.DELETE_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${CAMERAS_API_URL}/${cameraId}`,
    },
    cameraId,
  },
  meta,
});

export const deleteCameraSuccess = (cameraId: string, meta: MetaAction = {}): CameraTypes.DeleteCameraSuccessAction => ({
  type: types.DELETE_SUCCESS,
  payload: cameraId,
  meta,
});

export const deleteCameraFailure = (
  error: ApiError,
  meta: MetaAction = {}
): CameraTypes.DeleteCameraFailureAction => ({
  type: types.DELETE_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchCameraScans = (
  params: CameraTypes.CameraScansQueryParams,
  meta: MetaAction = {}
): CameraTypes.FetchCameraScansAction => ({
  type: types.FETCH_CAMERA_SCANS,
  payload: {
    cameraId: params.cameraId,
    request: {
      method: 'GET',
      url: `${CAMERAS_API_URL}/${params.cameraId}/scans`,
      params,
    },
  },
  meta,
});

export const fetchCameraScansSuccess = (
  scans: Array<CameraTypes.CameraScan>,
  cameraId: string,
  meta: MetaAction = {}
): CameraTypes.FetchCameraScansSuccessAction => ({
  type: types.FETCH_CAMERA_SCANS_SUCCESS,
  payload: { scans, cameraId },
  meta,
});

export const fetchCamerasUnlinkedHardware = (
  params: Record<string, unknown>,
  meta: MetaAction = {}
): CameraTypes.FetchUnlinkedHardwareAction => ({
  type: types.FETCH_CAMERAS_UNLINKED_HARDWARE,
  payload: {
    request: {
      method: 'GET',
      url: `${CAMERAS_API_URL}/unlinked-hardware`,
      params,
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

export const clearCameraDashboardFilters = (meta: MetaAction = {}): CameraTypes.ClearCameraDashboardFiltersAction => ({
  type: types.CLEAR_CAMERAS_DASHBOARD_FILTER,
  meta,
});

export const setCameraDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {}
): CameraTypes.SetCameraDashboardFilterAction => ({
  type: types.SET_CAMERAS_DASHBOARD_FILTER,
  payload: { field, value },
  meta,
});

export const removeCameraDashboardFilter = (
  field: string,
  value: unknown,
  meta: MetaAction = {}
): CameraTypes.RemoveCameraDashboardFilterAction => ({
  type: types.REMOVE_CAMERAS_DASHBOARD_FILTER,
  payload: {
    field,
    value,
  },
  meta,
});

export const setIsReady = (meta: MetaAction = {}): CameraTypes.SetIsReadyAction => ({
  type: types.SET_IS_READY,
  meta,
});

export const openPairingModal = (tvId: string, meta: MetaAction = {}): CameraTypes.OpenPairingModalAction => ({
  type: types.OPEN_PAIRING_MODAL,
  payload: tvId,
  meta,
});

export const closePairingModal = (meta: MetaAction = {}): CameraTypes.ClosePairingModalAction => ({
  type: types.CLOSE_PAIRING_MODAL,
  meta,
});
